import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { Error } from "@livechat/design-system-icons";
import { IPickerListItem, Icon, Picker } from "@livechat/design-system-react-components";

import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import { PickerContainerWithLabel } from "routes/automations/shared/components/PickerContainerWithLabel";
import { IRuleTriggerTeamChanged } from "store/entities/rules/interfaces";
import { ITeam, selectTeams, selectTeamsAlphabetically } from "store/entities/teams";

import { TRIGGER_ANY_OPTION_KEY, changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerTeamChanged>;
}

export const TeamChangedTrigger: FC<IProps> = ({ input }) => {
  const teamsById = useSelector(selectTeams);
  const teams = useSelector(selectTeamsAlphabetically);

  const { oldTeamIn, newTeamIn } = input.value;

  const teamOptions = [ANY_OPTION, ...teams.map(team => createTeamOption(team))];

  const selectedFromTeamOptions = oldTeamIn
    ? oldTeamIn.map(id => {
        const team = teamsById[id];
        return team ? createTeamOption(team) : createRemovedTeamOption(id);
      })
    : [ANY_OPTION];

  const selectedToTeamOptions = newTeamIn
    ? newTeamIn.map(id => {
        const team = teamsById[id];
        return team ? createTeamOption(team) : createRemovedTeamOption(id);
      })
    : [ANY_OPTION];

  const handleSelectTeam =
    (property: "oldTeamIn" | "newTeamIn", previousSelected: IPickerListItem[]) =>
    (selectedOptions: IPickerListItem[] | null): void => {
      if (!selectedOptions) {
        changeTriggerValues(input, { [property]: [] });
        return;
      }

      const currentOptionKeys = selectedOptions.map(opt => opt.key);
      const previousOptionKeys = previousSelected.map(opt => opt.key);

      const isAnySelected = currentOptionKeys.includes(TRIGGER_ANY_OPTION_KEY);
      const isAnyPreviouslySelected = previousOptionKeys.includes(TRIGGER_ANY_OPTION_KEY);

      let teamIn: string[] | undefined;

      if (isAnySelected) {
        teamIn = isAnyPreviouslySelected
          ? currentOptionKeys.filter(key => key !== TRIGGER_ANY_OPTION_KEY).map(key => key)
          : undefined;
      } else {
        teamIn = currentOptionKeys;
      }

      changeTriggerValues(input, { [property]: teamIn });
    };

  const handleSelectFromTeam = handleSelectTeam("oldTeamIn", selectedFromTeamOptions);

  const handleSelectToTeam = handleSelectTeam("newTeamIn", selectedToTeamOptions);

  return (
    <>
      <PickerContainerWithLabel label="from">
        <Picker
          type="multi"
          options={teamOptions}
          selected={selectedFromTeamOptions}
          onSelect={handleSelectFromTeam}
          placeholder="Select one team or more…"
        />
      </PickerContainerWithLabel>
      <PickerContainerWithLabel label="to">
        <Picker
          type="multi"
          options={teamOptions}
          selected={selectedToTeamOptions}
          onSelect={handleSelectToTeam}
          placeholder="Select one team or more…"
        />
      </PickerContainerWithLabel>
    </>
  );
};

const ANY_OPTION: IPickerListItem = {
  key: TRIGGER_ANY_OPTION_KEY,
  name: "Any team",
};

const createTeamOption = (team: ITeam): IPickerListItem => ({
  key: team.id,
  name: team.name,
  customElement: {
    selectedItemBody: <TeamPickerSelectedListItem name={team.name} isDefault={team.isDefault} pickerType="multi" />,
    listItemBody: <TeamPickerListItem name={team.name} isDefault={team.isDefault} pickerType="multi" />,
  },
});

const createRemovedTeamOption = (teamId: string): IPickerListItem => ({
  key: teamId,
  name: "Team removed",
  selectedTagOptions: {
    kind: "error",
    leftNode: <Icon source={Error} />,
  },
});
